import React, { useState } from 'react';
import {
  LayoutDashboard, MessageSquare, DollarSign,
  Upload, Package, Wifi, Settings, UserCog,
  FileText, BarChart2, ChevronDown, TrendingUp, ArrowDownCircle,
  ArrowUpCircle, BarChart3, Building2, Tag, Landmark, Boxes,
  CalendarClock, Target, Cpu, CreditCard, Power, Phone, LogOut, ShoppingCart, LayoutGrid, Edit2, LayoutTemplate, Truck,
  Repeat, } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { podeAcessarAba } from '../../utils/permissoes';

const MODULO_ICONS = {
  dashboard: LayoutDashboard,
  atendimento: MessageSquare,
  'templates-meta': LayoutTemplate,
  'crm-recompra': Repeat,
  'config-ia': Cpu,
  financeiro: DollarSign,
  disparo: Upload,
  vendas: ShoppingCart,
  estoque: Boxes,
  cadastros: Package,
  whatsapp: Wifi,
  instancias: Wifi,
  configuracoes: Settings,
  usuarios: UserCog,
  logs: FileText,
  relatorios: BarChart2,
  financeira: Target,
  'cadastro-produtos': CreditCard,
  promotora: Building2,
  'metricas-meta': BarChart2,
  'entregas': Truck,
};

export const MODULO_LABELS = {
  dashboard: 'Dashboard',
  atendimento: 'Atendimento',
  'crm-recompra': 'CRM Recompra',
  analises: 'Análises',
  'config-ia': 'Config IA',
  financeiro: 'Financeiro',
  disparo: 'Disparo',
  vendas: 'Vendas',
  estoque: 'Estoque',
  cadastros: 'Cadastro',
  whatsapp: 'WhatsApp',
  instancias: 'Instâncias WA',
  configuracoes: 'Configurações',
  usuarios: 'Usuários',
  logs: 'Logs',
  relatorios: 'Relatórios',
  financeira: 'Financeira',
  'cadastro-produtos': 'Produtos/Banco',
  'templates-meta': 'Templates Meta',
  'metricas-meta':  'Métricas Meta',
  'entregas':       'Entregas',
};

const SUB_DISPARO = [
  { id: 'grupo',         label: 'Por Grupo',     icon: LayoutGrid },
  { id: 'personalizado', label: 'Personalizado', icon: Edit2 },
  { id: 'progresso',     label: 'Acompanhamento', icon: BarChart2 },
  { id: 'agendamento',   label: 'Agendamento',   icon: CalendarClock },
];

const SUB_FINANCEIRO = [
  { id: 'dashboard',       label: 'Dashboard',    icon: TrendingUp },
  { id: 'contas_pagar',    label: 'A Pagar',      icon: ArrowDownCircle },
  { id: 'contas_receber',  label: 'A Receber',    icon: ArrowUpCircle },
  { id: 'fluxo_caixa',     label: 'Caixa',        icon: BarChart3 },
  { id: 'fornecedores',    label: 'Fornecedores', icon: Building2 },
  { id: 'categorias',      label: 'Categorias',   icon: Tag },
  { id: 'contas_bancarias',label: 'Contas',       icon: Landmark },
  { id: 'despesas',        label: 'Despesas',     icon: CreditCard },
  { id: 'dre',             label: 'DRE',          icon: BarChart2 },
];

const SUB_FINANCEIRA = [
  { id: 'cadastro-produtos', label: 'Produtos/Banco', icon: CreditCard },
];

// IDs de sub-módulos da financeira (para detectar e agrupar em legado)
const FINANCEIRA_SUB_IDS = new Set(SUB_FINANCEIRA.map(s => s.id));

function NavItem({ id, label, Icon, ativo, onClick, badge }) {
  return (
    <button
      onClick={() => onClick(id)}
      className={cn(
        'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors text-left',
        ativo
          ? 'bg-blue-50 text-blue-700'
          : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
      )}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0" />}
      <span className="flex-1 truncate">{label}</span>
      {badge > 0 && (
        <Badge className="h-[18px] min-w-[18px] text-[11px] px-1.5 rounded-full bg-red-500 hover:bg-red-500 text-white justify-center">{badge > 99 ? '99+' : badge}</Badge>
      )}
    </button>
  );
}

function SubNavItem({ id, label, icon: Icon, ativo, onClick }) {
  return (
    <button
      onClick={() => onClick(id)}
      className={cn(
        'w-full flex items-center gap-2 pl-9 pr-3 py-2 rounded-xl text-[13px] transition-colors text-left',
        ativo
          ? 'text-blue-700 font-semibold bg-blue-50'
          : 'text-slate-500 font-medium hover:text-slate-800 hover:bg-slate-50'
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
      <span className="truncate">{label}</span>
    </button>
  );
}

export default function AppSidebar({
  abaAtiva,
  onNavegar,
  modulos = [],
  perfil,
  totalNaoLidas = 0,
  subAbaDisparoAtiva,
  onSubAbaDisparo,
  subAbaFinanceiroAtiva,
  onSubAbaFinanceiro,
  atendenteAtivo = false,
  atendenteProcessando = false,
  onToggleAtendente,
  onLogout,
}) {
  const financeiraAtivaAgora = FINANCEIRA_SUB_IDS.has(abaAtiva);

  const [disparoOpen, setDisparoOpen] = useState(abaAtiva === 'disparo');
  const [financeiroOpen, setFinanceiroOpen] = useState(abaAtiva === 'financeiro');
  const [financeiraOpen, setFinanceiraOpen] = useState(financeiraAtivaAgora);

  // Normaliza: substitui IDs individuais da financeira por um único 'financeira'
  // para poder renderizar o collapsible — suporta tanto legado quanto planos novos
  const MODULOS_ADMIN = ['usuarios', 'logs'];
  const modulosNormalizados = modulos.reduce((acc, m) => {
    if (FINANCEIRA_SUB_IDS.has(m)) {
      if (!acc.includes('financeira')) acc.push('financeira');
      return acc;
    }
    acc.push(m);
    return acc;
  }, []);

  // Sub-módulos da financeira que este tenant tem E o perfil pode acessar
  const financeiraSubs = SUB_FINANCEIRA.filter(s => modulos.includes(s.id) && podeAcessarAba({ perfil }, s.id));

  const modulosPrincipais = modulosNormalizados.filter(m => !MODULOS_ADMIN.includes(m));
  const modulosAdmin = modulosNormalizados.filter(m => MODULOS_ADMIN.includes(m));

  return (
    <aside className="w-[248px] flex-1 min-h-0 bg-white flex flex-col overflow-hidden">
      <nav className="flex-1 overflow-y-auto px-4 py-5 space-y-0.5">
        {modulosPrincipais.map(mod => {
          // 'financeira' é um ID virtual (grupo), checa pelos sub-módulos individuais
          if (mod === 'financeira') {
            if (!financeiraSubs.some(s => podeAcessarAba({ perfil }, s.id))) return null;
          } else if (!podeAcessarAba({ perfil }, mod)) {
            return null;
          }

          if (mod === 'disparo') {
            return (
              <Collapsible key={mod} open={disparoOpen} onOpenChange={setDisparoOpen}>
                <CollapsibleTrigger asChild>
                  <button className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors',
                    abaAtiva === 'disparo'
                      ? 'text-blue-700'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                  )}>
                    <Upload className="h-4 w-4 shrink-0" />
                    <span className="flex-1 text-left">Disparo</span>
                    <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', disparoOpen && 'rotate-180')} />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="animate-collapsible-down space-y-0.5 mt-0.5">
                  {SUB_DISPARO.map(sub => (
                    <SubNavItem
                      key={sub.id}
                      {...sub}
                      ativo={abaAtiva === 'disparo' && subAbaDisparoAtiva === sub.id}
                      onClick={(id) => { onNavegar('disparo'); onSubAbaDisparo?.(id); }}
                    />
                  ))}
                </CollapsibleContent>
              </Collapsible>
            );
          }

          if (mod === 'financeiro') {
            return (
              <Collapsible key={mod} open={financeiroOpen} onOpenChange={setFinanceiroOpen}>
                <CollapsibleTrigger asChild>
                  <button className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors',
                    abaAtiva === 'financeiro'
                      ? 'text-blue-700'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                  )}>
                    <DollarSign className="h-4 w-4 shrink-0" />
                    <span className="flex-1 text-left">Financeiro</span>
                    <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', financeiroOpen && 'rotate-180')} />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="animate-collapsible-down space-y-0.5 mt-0.5">
                  {SUB_FINANCEIRO.map(sub => (
                    <SubNavItem
                      key={sub.id}
                      {...sub}
                      ativo={abaAtiva === 'financeiro' && subAbaFinanceiroAtiva === sub.id}
                      onClick={(id) => { onNavegar('financeiro'); onSubAbaFinanceiro?.(id); }}
                    />
                  ))}
                </CollapsibleContent>
              </Collapsible>
            );
          }

          if (mod === 'financeira') {
            return (
              <Collapsible key={mod} open={financeiraOpen} onOpenChange={setFinanceiraOpen}>
                <CollapsibleTrigger asChild>
                  <button className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors',
                    financeiraAtivaAgora
                      ? 'text-blue-700'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                  )}>
                    <Target className="h-4 w-4 shrink-0" />
                    <span className="flex-1 text-left">Financeira</span>
                    <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', financeiraOpen && 'rotate-180')} />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="animate-collapsible-down space-y-0.5 mt-0.5">
                  {financeiraSubs.map(sub => (
                    <SubNavItem
                      key={sub.id}
                      {...sub}
                      ativo={abaAtiva === sub.id}
                      onClick={onNavegar}
                    />
                  ))}
                </CollapsibleContent>
              </Collapsible>
            );
          }

          const Icon = MODULO_ICONS[mod];
          return (
            <NavItem
              key={mod}
              id={mod}
              label={MODULO_LABELS[mod] || mod}
              Icon={Icon}
              ativo={abaAtiva === mod}
              onClick={onNavegar}
              badge={mod === 'atendimento' ? totalNaoLidas : 0}
            />
          );
        })}

        {modulosAdmin.length > 0 && podeAcessarAba({ perfil }, 'usuarios') && (
          <>
            <Separator className="my-2" />
            <p className="px-3 text-[11px] font-semibold text-slate-400 uppercase tracking-[0.08em] mb-1">Admin</p>
            {modulosAdmin.map(mod => {
              if (!podeAcessarAba({ perfil }, mod)) return null;
              const Icon = MODULO_ICONS[mod];
              return (
                <NavItem
                  key={mod}
                  id={mod}
                  label={MODULO_LABELS[mod] || mod}
                  Icon={Icon}
                  ativo={abaAtiva === mod}
                  onClick={onNavegar}
                />
              );
            })}
          </>
        )}
      </nav>

      {/* Rodapé fixo */}
      <div className="flex-shrink-0 border-t border-slate-200 p-4 space-y-2">
        {/* Toggle IA — só para não-vendedor */}
        {perfil !== 'vendedor' && (
          <>
            <div className="flex items-center gap-2 px-1 mb-1">
              <Cpu className={cn('h-3.5 w-3.5 shrink-0', atendenteAtivo ? 'text-green-500' : 'text-slate-400')} />
              <span className="text-xs text-slate-500 font-medium">Atendente Virtual</span>
              {atendenteAtivo && (
                <span className="ml-auto flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-green-400 opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                </span>
              )}
            </div>
            <button
              onClick={onToggleAtendente}
              disabled={atendenteProcessando}
              className={cn(
                'w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-bold transition-colors',
                atendenteProcessando && 'opacity-50 cursor-not-allowed',
                atendenteAtivo
                  ? 'bg-green-500 hover:bg-green-600 text-white'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
              )}
            >
              {atendenteProcessando ? (
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current" />
              ) : (
                <Power className="h-3 w-3" />
              )}
              {atendenteAtivo ? 'LIGADO' : 'DESLIGADO'}
            </button>

            {/* Link app mobile */}
            <a
              href="https://integraiapp.greentechsistemas.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-xl text-xs font-semibold transition-colors"
            >
              <Phone className="h-3.5 w-3.5" />
              Abrir App Mobile
            </a>
          </>
        )}

        {/* Logout */}
        <button
          onClick={onLogout}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-xs font-semibold transition-colors"
        >
          <LogOut className="h-3.5 w-3.5" />
          Sair
        </button>
      </div>
    </aside>
  );
}
